import { type ComponentProps, type FC, useEffect, useRef, useState, useMemo } from "react";
import { Icon } from "@root/shared/components/Icon";
import { TextButton } from "@root/shared/components/TextButton";
import { InformationText } from "@root/shared/components/InformationText";
import { InformationCard } from "@userFrontend/components/InformationCard";
import { ReadMoreButton } from "@root/shared/components/ReadMoreButton";
import { SearchModal } from "@userFrontend/features/clientReview/components/ClientReviewInputs/uiParts/SearchModal";
import { JobEntryRadioButton } from "@userFrontend/components/JobEntryRadioButton";
import { ErrorInformationText } from "@userFrontend/components/ErrorInformationText";
import { type SiteId } from "@root/shared/types/type";
import { type ErrorText } from "@userFrontend/features/reward/components/ContinuedRewardInputs/type";
import {
  type Client,
  type ClientShop,
} from "@userFrontend/features/clientReview/components/ClientReviewInputs/type";
import { ClientSelectInfoBox } from "@userFrontend/features/report/components/ClientSelectSection/uiParts/ClientSelectInfoBox";
import * as styles from "./styles.css";

type Props = {
  isShowAnnotation?: boolean;
  client?: Client;
  clientShop?: ClientShop;
  handleShowModal: () => void;
  checkedJobEntryId: number | null;
  jobEntryButtons: (Pick<
    ComponentProps<typeof JobEntryRadioButton>,
    "clientName" | "brandName" | "workTitle" | "jobEntryId"
  > & {
    siteId: SiteId;
  })[];
  clientErrorText: ErrorText;
} & ComponentProps<typeof SearchModal>;

export const EntryClientSelect: FC<Props> = ({
  isShowAnnotation = true,
  client,
  clientShop,
  checkedJobEntryId,
  jobEntryButtons,
  clientErrorText,
  isShowSearchModal,
  handleCloseModal,
  clientNameValue,
  shopNameValue,
  clientNameErrorText,
  shopNameErrorText,
  handleClientSearch,
  handleChangeClientNameValue,
  handleChangeShopNameValue,
  searchResults,
  handleClientSelect,
  isShowClientsDetail,
  handleShowTextButton,
  tooManyResultErrorText,
  noResultError,
  displaySearchText,
  isSearching,
  handleReturnToMyPageTop,
  userContactPath,
  handleShowModal,
}) => {
  const [containerHeight, setContainerHeight] = useState<number | undefined>(undefined);
  const jobEntryRefs = useRef<(HTMLDivElement | null)[]>([]);
  const showDeclineDetailsReadMoreButton = useMemo(
    () => jobEntryButtons.length >= 3,
    [jobEntryButtons],
  );

  useEffect(() => {
    if (
      jobEntryRefs.current[0] &&
      jobEntryRefs.current[1] &&
      jobEntryRefs.current[2] &&
      setContainerHeight
    ) {
      const totalRefHeight =
        jobEntryRefs.current[0].offsetHeight +
        jobEntryRefs.current[1].offsetHeight +
        jobEntryRefs.current[2].offsetHeight;
      setContainerHeight(totalRefHeight);
    }
  }, [jobEntryButtons, setContainerHeight]);

  const renderJobEntryButtons = () =>
    jobEntryButtons.map(({ clientName, brandName, workTitle, jobEntryId, siteId }, index) => (
      <div key={jobEntryId} ref={(el) => (jobEntryRefs.current[index] = el)}>
        <JobEntryRadioButton
          brandName={brandName}
          clientName={clientName}
          workTitle={workTitle}
          jobEntryId={jobEntryId}
          disabled={false}
          isSp
          checked={checkedJobEntryId === jobEntryId}
          isError={!!clientErrorText}
          handleChangeEntryClient={() =>
            handleClientSelect({
              selectedSiteId: siteId,
              clientName,
              params: { jobEntryId },
              brandName,
              workTitle,
            })
          }
          name="jobEntry"
        />
      </div>
    ));

  return (
    <>
      <InformationCard>
        <p className={styles.text}>リジョブ経由で採用</p>
        <div className={styles.buttonWrapper}>
          {showDeclineDetailsReadMoreButton ? (
            <ReadMoreButton size="large" containerMaxHeight={containerHeight}>
              <>{renderJobEntryButtons()}</>
            </ReadMoreButton>
          ) : (
            <>{renderJobEntryButtons()}</>
          )}
        </div>
      </InformationCard>

      <div>
        {client?.clientName && client?.clientId ? (
          <div className={styles.clientSelectInfoBoxWrapper}>
            <ClientSelectInfoBox
              clientName={client?.clientName}
              clientShopName={clientShop?.shopName}
              onClick={handleShowModal}
            />
          </div>
        ) : (
          <div className={styles.box}>
            <div className={styles.boxInner}>
              <p>上記以外から選ぶ</p>
              <TextButton onClick={handleShowModal} size="small" rightIconName="arrow-light-right">
                投稿先を検索する
              </TextButton>
            </div>
          </div>
        )}
        <SearchModal
          isShowSearchModal={isShowSearchModal}
          handleCloseModal={handleCloseModal}
          clientNameValue={clientNameValue}
          shopNameValue={shopNameValue}
          clientNameErrorText={clientNameErrorText}
          shopNameErrorText={shopNameErrorText}
          handleClientSearch={handleClientSearch}
          handleChangeClientNameValue={handleChangeClientNameValue}
          handleChangeShopNameValue={handleChangeShopNameValue}
          searchResults={searchResults}
          handleClientSelect={handleClientSelect}
          isShowClientsDetail={isShowClientsDetail}
          handleShowTextButton={handleShowTextButton}
          tooManyResultErrorText={tooManyResultErrorText}
          noResultError={noResultError}
          displaySearchText={displaySearchText}
          isSearching={isSearching}
          handleReturnToMyPageTop={handleReturnToMyPageTop}
          userContactPath={userContactPath}
        />
      </div>

      {clientErrorText && <ErrorInformationText text={clientErrorText} />}
      {isShowAnnotation && (
        <InformationText
          icon={<Icon name="information" />}
          text={<>リジョブに求人を掲載中の企業のみ、投稿を受け付けています。</>}
          className={styles.informationText}
        />
      )}
    </>
  );
};
